import styles from "./ResultTable.module.scss";

import { useEffect, useMemo, useState } from "react";
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  flexRender,
  SortingState,
  ColumnDef,
  PaginationState,
} from "@tanstack/react-table";
import classNames from "classnames";
import { useSearchParams } from "react-router-dom";

// components
import { Table } from "@trussworks/react-uswds";
import Pagination from "./Pagination";

// types
import { BusinessItem } from "types/results-panel.types";

// columns
import { columns, optionalColumns } from "./Columns";

// store
import useColumnsStore from "store/useColumnsStore";
// import { useSearchStore } from "store/searchStore";

type ResultTableProps = {
  data: BusinessItem[];
  sortType: string;
  setItemsPerPage?: React.Dispatch<React.SetStateAction<number>>;
  rootClassname?: string;
  tableClassname?: string;
  totalPages?: number;
  setSortedTableData?: React.Dispatch<React.SetStateAction<BusinessItem[]>>;
};

export default function ResultTable(props: ResultTableProps) {
  const { data, sortType, setItemsPerPage, rootClassname, tableClassname, totalPages, setSortedTableData } =
    props;

  const { storeColumns } = useColumnsStore((state) => ({
    storeColumns: state.columns,
  }));

  // const { pageIndex, setPageIndex } = useSearchStore((state) => ({
  //   pageIndex: state.pageIndex,
  //   setPageIndex: state.setPageIndex,
  // }));

  const [sorting, setSorting] = useState<SortingState>([]);

  const [searchParams, setSearchParams] = useSearchParams();

  const initialPageIndex = parseInt(searchParams.get("page") || "0", 10);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const rootClass = classNames(styles["table-root"], {
    [rootClassname]: rootClassname,
  });

  const tableClass = classNames(styles["table"], {
    [tableClassname]: tableClassname,
  });

  const dynamicColumns = useMemo(() => {
    const columnMap = new Map<string, ColumnDef<BusinessItem>>(
      columns.map((column) => [column.id, column])
    );

    if (storeColumns.length === 0) {
      return Array.from(columnMap.values());
    }
    // Apply modifications from store columns
    storeColumns.forEach((item) => {
      if (columnMap.has(item.name)) {
        // Update existing column
        const column = columnMap.get(item.name);
        columnMap.set(item.name, column);
      } else {
        // Add new column if not found
        columnMap.set(item.name,
          optionalColumns.find((col) => col?.id === item.name))
      }
    });

    // Remove columns that are no longer in editTablecolumnData but were added previously
    columnMap.forEach((_, key) => {
      if (!storeColumns.find((item) => item.name === key)) {
        columnMap.delete(key);
      }
    });

    return Array.from(columnMap.values());
  }, [storeColumns, columns]);

  const table = useReactTable({
    initialState: {
      pagination: {
        pageSize: 25,
        pageIndex: initialPageIndex > 0 ? initialPageIndex - 1 : 0,
      },
      sorting: [{ id: "businessName", desc: false }],
      columnVisibility: {
        _rankingScore: false
      }
    },
    state: {
      pagination,
      sorting,
    },
    data,
    columns: dynamicColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: (newPagination) => {
      const resolvedPagination =
        typeof newPagination === "function"
          ? newPagination(pagination)
          : newPagination;

      setPagination(resolvedPagination);

      // Update URL query parameter
      setSearchParams({ page: (resolvedPagination.pageIndex + 1).toString() });
    },
  });

  // sort table
  useEffect(() => {
    if (sortType === "alphabetical") {
      setSorting([{ id: "legal_business_name", desc: false }]);
    } else if (sortType === "recently_updated") {
      setSorting([{ id: "last_update_date", desc: true }]);
    } else if (sortType === "relevance") {
      setSorting([{ id: "_rankingScore", desc: true }]);
    } else {
      setSorting([]);
    }
  }, [sortType]);

  // set items per page
  useEffect(() => {
    if (setItemsPerPage)
      setItemsPerPage(table.getPaginationRowModel().rows.length);
  }, [table.getPaginationRowModel()]);

  useEffect(() => {
    const sortedRows = table.getSortedRowModel().rows;
    setSortedTableData?.(sortedRows.map((row) => row.original));
  }, [table.getSortedRowModel()]);

  // const tablePageIndex = table.getState().pagination.pageIndex;
  // console.log("tablePageIndex", tablePageIndex);

  // useEffect(() => {
  //   // setPageIndex(tablePageIndex);
  // }, [tablePageIndex]);

  return (
    <>
      <div className={rootClass} tabIndex={0}>
        <Table bordered className={tableClass} stackedStyle="headers">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} scope="col">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <Pagination table={table} totalPages={totalPages} />
    </>
  );
}
